import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
} from "recharts";
import {
  ArrowRight,
  CheckCircle2,
  Download,
  Loader2,
  Mail,
  MessagesSquare,
  Sparkles,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { WeaveLogo } from "@/components/WeaveLogo";
import { FadeUp, Overline } from "@/components/Motion";
import { getReport, getComparison, emailReport, pdfUrl } from "@/lib/api";
import { DepositButton } from "@/components/DepositButton";
import { ProspectusDialog } from "@/components/ProspectusDialog";

const SHORT_NAMES = {
  inner_stability: "Inner",
  cognitive: "Cognitive",
  physical: "Physical",
  adaptive: "Adaptive",
  relational: "Relational",
  purposeful_action: "Action",
  founder_transition: "Transition",
};

const BAND_STYLES = {
  low: "border-weave-red/50 text-weave-red bg-weave-red/5",
  medium: "border-weave-yellow/70 text-[#8a6a1b] bg-weave-yellow/10",
  high: "border-weave-teal/50 text-weave-teal bg-weave-teal/5",
  incomplete: "border-weave-line text-weave-mute bg-weave-sand",
};

const TIDYCAL_URL = "https://tidycal.com/the-weave/30-minute-meeting";

export default function Results() {
  const { id } = useParams();
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [comparison, setComparison] = useState(null);

  useEffect(() => {
    getReport(id)
      .then((d) => {
        setPayload(d);
        if (d.respondent?.email) setEmailInput(d.respondent.email);
      })
      .catch(() => setError("We couldn't find that report. It may not have been completed yet."));
    getComparison(id)
      .then((d) => setComparison(d.comparison))
      .catch(() => {});
  }, [id]);

  const report = payload?.report;
  const prevMap = useMemo(
    () => Object.fromEntries((comparison?.capacities || []).map((c) => [c.id, c.display])),
    [comparison]
  );
  const radarData = useMemo(
    () =>
      (report?.capacities || []).map((c) => ({
        name: SHORT_NAMES[c.id] || c.name,
        full: c.name,
        value: c.mean ? Number(c.mean.toFixed(2)) : 0,
        ...(comparison ? { prev: prevMap[c.id] ?? 0 } : {}),
      })),
    [report, comparison, prevMap]
  );
  const comparisonSummary = useMemo(() => {
    if (!comparison || !report) return null;
    const delta = Number((report.overall_display - comparison.overall_display).toFixed(1));
    const curMap = Object.fromEntries(report.capacities.map((c) => [c.id, c.display ?? 0]));
    let mover = null;
    let moverDelta = 0;
    for (const c of comparison.capacities) {
      const d = Number(((curMap[c.id] ?? 0) - c.display).toFixed(1));
      if (Math.abs(d) > Math.abs(moverDelta)) {
        moverDelta = d;
        mover = c.name;
      }
    }
    return { delta, mover, moverDelta };
  }, [comparison, report]);

  const sendEmail = async () => {
    if (!emailInput) {
      toast.error("Add your email address first.");
      return;
    }
    setEmailBusy(true);
    try {
      await emailReport(id, emailInput);
      setEmailSent(true);
      toast.success("Your report is on its way.");
    } catch {
      toast.error("Could not send the email — please try again.");
    } finally {
      setEmailBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6 text-center" data-testid="results-error">
        <WeaveLogo />
        <p className="text-weave-slate max-w-md">{error}</p>
        <Link to="/assessment" className="inline-flex items-center gap-2 bg-weave-teal text-white rounded-full px-8 py-4 font-medium hover:bg-weave-tealdeep transition-colors duration-300">
          Take the Index <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-weave-slate" data-testid="results-loading">
        <Loader2 className="animate-spin" size={28} />
        <p className="text-sm">Loading your snapshot…</p>
      </div>
    );
  }

  const firstName = payload.respondent?.first_name;
  const respondentEmail = payload.respondent?.email || "";
  const completedDate = payload.completed_at ? new Date(payload.completed_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <div data-testid="results-page">
      <header className="border-b border-weave-line bg-weave-cream/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" aria-label="Back to home"><WeaveLogo /></Link>
          <a
            href={pdfUrl(id)}
            data-testid="header-download-pdf-button"
            className="inline-flex items-center gap-2 bg-weave-ink text-white hover:bg-weave-ink/90 transition-colors duration-300 rounded-full px-5 py-2.5 text-sm font-medium"
          >
            <Download size={15} /> PDF report
          </a>
        </div>
      </header>

      {/* Summary */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeUp>
            <Overline>Your Founder Capacity snapshot</Overline>
            <h1 className="mt-4 text-4xl md:text-5xl font-light tracking-tight text-weave-ink" data-testid="results-heading">
              {firstName ? `${firstName}, here is` : "Here is"} your pattern{firstName ? "." : ""}
            </h1>
            <p className="mt-4 text-base text-weave-slate max-w-2xl leading-relaxed">
              Completed {completedDate}. A developmental snapshot — not a verdict. Scores can change
              with practice, and you can repeat the Index later to see what has shifted.
            </p>
          </FadeUp>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-12">
            <FadeUp className="lg:col-span-7 bg-white border border-weave-line rounded-3xl p-6 md:p-10" data-testid="radar-card">
              <h2 className="text-xl md:text-2xl font-medium text-weave-ink">Your Founder Capacity Wheel</h2>
              <p className="text-sm text-weave-mute mt-1">Fixed 1–5 scale · means to one decimal</p>
              <div className="mt-4" data-testid="radar-chart">
                <ResponsiveContainer width="100%" height={400}>
                  <RadarChart data={radarData} outerRadius="70%">
                    <PolarGrid stroke="#E5DFD3" />
                    <PolarAngleAxis dataKey="name" tick={{ fill: "#4A5D5E", fontSize: 12, fontFamily: "Rubik" }} />
                    <PolarRadiusAxis domain={[0, 5]} tickCount={6} tick={{ fill: "#8A999A", fontSize: 10 }} axisLine={false} />
                    {comparison && (
                      <Radar
                        dataKey="prev"
                        name="Previous"
                        stroke="#8A999A"
                        strokeWidth={2}
                        strokeDasharray="5 4"
                        fill="rgba(138,153,154,0.12)"
                        dot={false}
                        isAnimationActive
                      />
                    )}
                    <Radar
                      dataKey="value"
                      name="Today"
                      stroke="#4aa0a4"
                      strokeWidth={2.5}
                      fill="rgba(74,160,164,0.22)"
                      dot={{ r: 4, fill: "#f4b847", stroke: "#0D2C2D", strokeWidth: 1 }}
                      isAnimationActive
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <ul className="sr-only" data-testid="radar-text-equivalent">
                {report.capacities.map((c) => (
                  <li key={c.id}>{c.name}: {c.display ?? "not scored"} out of 5</li>
                ))}
              </ul>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                {report.capacities.map((c) => (
                  <div key={c.id} className="rounded-xl border border-weave-line px-3 py-2.5" data-testid={`score-chip-${c.id}`}>
                    <p className="text-[11px] text-weave-mute leading-tight">{SHORT_NAMES[c.id]}</p>
                    <p className="text-base font-bold text-weave-ink">{c.display ?? "—"}<span className="text-xs font-normal text-weave-mute">/5</span></p>
                  </div>
                ))}
              </div>
              {comparison && comparisonSummary && (
                <div className="mt-5 rounded-xl bg-weave-sand/70 border border-weave-line p-5" data-testid="comparison-strip">
                  <div className="flex items-center gap-4 text-xs text-weave-mute mb-3">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-3 h-0.5 bg-weave-teal inline-block rounded" /> Today
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-3 h-0.5 inline-block rounded" style={{ backgroundImage: "linear-gradient(90deg, #8A999A 60%, transparent 40%)", backgroundSize: "5px 2px" }} />{" "}
                      Previous — {new Date(comparison.completed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  </div>
                  <p className="text-sm text-weave-slate leading-relaxed">
                    <strong className="text-weave-ink font-medium">Since your last snapshot:</strong>{" "}
                    overall {comparison.overall_display} → {report.overall_display} (
                    {comparisonSummary.delta >= 0 ? "+" : ""}{comparisonSummary.delta})
                    {comparisonSummary.mover && Math.abs(comparisonSummary.moverDelta) > 0 && (
                      <> · biggest mover: {comparisonSummary.mover} ({comparisonSummary.moverDelta >= 0 ? "+" : ""}{comparisonSummary.moverDelta})</>
                    )}
                    . Scores can change — here is the proof.
                  </p>
                </div>
              )}
            </FadeUp>

            <FadeUp delay={0.1} className="lg:col-span-5 bg-weave-ink text-weave-cream rounded-3xl p-8 md:p-10 flex flex-col" data-testid="glance-card">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-weave-yellow">At a glance</p>
              <div className="mt-6 flex items-end gap-2">
                <span className="text-6xl font-light tracking-tight" data-testid="overall-score">{report.overall_display}</span>
                <span className="text-weave-cream/60 mb-2">/5 overall mean</span>
              </div>

              {report.profile && (
                <div className="mt-6 border border-white/15 rounded-2xl p-5" data-testid="profile-badge">
                  <p className="flex items-center gap-2 text-weave-yellow text-sm font-medium"><Sparkles size={16} /> {report.profile.name}</p>
                  <p className="mt-2 text-sm text-weave-cream/70 leading-relaxed">{report.profile.description}</p>
                </div>
              )}

              <div className="mt-6">
                <p className="text-sm font-medium text-weave-teal">Strengths to draw on</p>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="strengths-list">
                  {report.strengths.map((s) => (
                    <span key={s.id} className="text-xs bg-weave-teal/20 text-weave-cream border border-weave-teal/40 rounded-full px-3 py-1.5">
                      {s.name} · {s.display}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-5">
                <p className="text-sm font-medium text-weave-red">Development focus</p>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="priorities-list">
                  {report.priorities.length > 0 ? (
                    report.priorities.map((p) => (
                      <span key={p.id} className="text-xs bg-weave-red/15 text-weave-cream border border-weave-red/40 rounded-full px-3 py-1.5">
                        {p.name} · {p.display}
                      </span>
                    ))
                  ) : report.next_edge ? (
                    <span className="text-xs bg-white/10 text-weave-cream border border-white/20 rounded-full px-3 py-1.5">
                      {report.next_edge.name} · {report.next_edge.display} — your next edge, not a weakness
                    </span>
                  ) : null}
                </div>
              </div>

              {report.spread_note && (
                <p className="mt-6 text-sm text-weave-cream/60 leading-relaxed" data-testid="spread-note">{report.spread_note}</p>
              )}
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Capacity cards */}
      <section className="py-16 md:py-24 bg-weave-sand/50 border-y border-weave-line">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeUp>
            <Overline>Capacity by capacity</Overline>
            <h2 className="mt-4 text-3xl md:text-4xl font-normal tracking-tight text-weave-ink">Where practice could help</h2>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
            {report.capacities.map((c, i) => {
              const lowest = c.questions.filter((q) => q.value).sort((a, b) => a.value - b.value).slice(0, 2);
              return (
                <FadeUp key={c.id} delay={(i % 2) * 0.08} className="bg-white border border-weave-line rounded-2xl p-8 flex flex-col" data-testid={`capacity-card-${c.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="text-xl md:text-2xl font-medium text-weave-ink">{c.name}</h3>
                    <span className={`shrink-0 text-xs font-bold rounded-full border px-3 py-1.5 ${BAND_STYLES[c.band]}`} data-testid={`band-pill-${c.id}`}>
                      {c.band_label}{c.provisional ? " · provisional" : ""}
                    </span>
                  </div>
                  <p className="mt-1 text-3xl font-light text-weave-ink" data-testid={`capacity-score-${c.id}`}>
                    {c.display ?? "—"}<span className="text-base text-weave-mute">/5</span>
                  </p>
                  <p className="mt-4 text-sm text-weave-slate leading-relaxed">{c.helps} {c.band_note}</p>
                  {c.need && <p className="mt-3 text-sm text-weave-slate leading-relaxed"><strong className="text-weave-ink font-medium">What you may need:</strong> {c.need}</p>}
                  <div className="mt-4 border-l-2 border-weave-teal pl-4">
                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-weave-teal">How the Academy responds</p>
                    <p className="mt-1.5 text-sm text-weave-slate leading-relaxed">{c.response}</p>
                  </div>
                  <div className="mt-4 border-l-2 border-weave-yellow pl-4">
                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#8a6a1b]">Try this now</p>
                    <p className="mt-1.5 text-sm text-weave-slate leading-relaxed">{c.practice}</p>
                  </div>
                  {lowest.length > 0 && (
                    <div className="mt-6 pt-5 border-t border-weave-line space-y-2.5">
                      {lowest.map((q) => (
                        <p key={q.id} className="text-xs text-weave-mute leading-relaxed">
                          <span className="font-bold text-weave-ink">{q.value}/5 · {q.label}</span> — “{q.text}”
                        </p>
                      ))}
                    </div>
                  )}
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pathway + reflection */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <FadeUp className="lg:col-span-7 bg-white border border-weave-line rounded-3xl p-8 md:p-10" data-testid="pathway-card">
            <Overline>Your recommended Academy pathway</Overline>
            <h2 className="mt-4 text-2xl md:text-3xl font-normal tracking-tight text-weave-ink">Mapped to your pattern, explained in plain language</h2>
            <div className="mt-8 space-y-6">
              {report.pathway.map((p, i) => (
                <div key={p.capacity} className="flex gap-5 items-start" data-testid={`pathway-item-${i}`}>
                  <span className="shrink-0 w-10 h-10 rounded-full bg-weave-sand flex items-center justify-center text-weave-red font-bold text-sm">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <p className="font-medium text-weave-ink">{p.capacity}</p>
                    <p className="mt-1 text-sm text-weave-slate leading-relaxed">{p.need} The Academy responds with: {p.response}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <ProspectusDialog
                triggerText="Download the Personal Clarity prospectus"
                testId="pathway-prospectus-button"
                triggerClassName="group inline-flex items-center gap-2 text-weave-teal font-medium"
              />
              <div className="flex flex-col items-start gap-3">
                <DepositButton email={respondentEmail} assessmentId={id} plan="clarity" testId="results-clarity-button" />
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.1} className="lg:col-span-5 bg-weave-yellow/15 border border-weave-yellow/50 rounded-3xl p-8 md:p-10" data-testid="reflection-card">
            <Overline>A 7-day reflection</Overline>
            <h2 className="mt-4 text-2xl md:text-3xl font-normal tracking-tight text-weave-ink">One small thing, before anything else</h2>
            {report.reflection && (
              <>
                <p className="mt-6 text-base text-weave-slate leading-relaxed">
                  For your <strong className="text-weave-ink font-medium">{report.reflection.capacity}</strong>:
                </p>
                <p className="mt-3 text-lg text-weave-ink leading-relaxed font-light">{report.reflection.practice}</p>
              </>
            )}
            <p className="mt-6 text-sm text-weave-slate leading-relaxed">
              Try it for seven days. No sales conversation required first — this one is just for you.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Next steps */}
      <section className="py-16 md:py-24 bg-weave-ink text-weave-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeUp>
            <Overline dark>Your next step</Overline>
            <h2 className="mt-4 text-3xl md:text-4xl font-light tracking-tight max-w-2xl">
              The snapshot is yours. <span className="text-weave-yellow">The practice ground is open.</span>
            </h2>
            <p className="mt-4 text-weave-cream/70 max-w-xl leading-relaxed">
              Choose whichever feels right — a conversation, ten weeks of Personal Clarity, or
              downloading the prospectus to explore at your own pace.
            </p>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12" data-testid="cta-grid">
            <FadeUp delay={0.05}>
              <a
                href={TIDYCAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-clarity-consult"
                className="group block w-full text-left bg-white/5 border border-white/15 rounded-2xl p-6 transition-all duration-300 hover:bg-white/10 hover:-translate-y-1"
              >
                <MessagesSquare size={22} className="text-weave-yellow" />
                <h3 className="mt-4 text-lg font-medium text-weave-cream leading-snug">Book a 30-minute Founder Clarity Consult</h3>
                <p className="mt-2 text-sm text-weave-cream/60 leading-relaxed">
                  A relaxed, no-pressure conversation about your results and what founder life could
                  look like for you — straight into our calendar.
                </p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-weave-teal">
                  Choose a time
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </a>
            </FadeUp>
            <FadeUp delay={0.05}>
              <div
                data-testid="cta-personal-clarity"
                className="block w-full text-left bg-white/5 border border-white/15 rounded-2xl p-6 transition-all duration-300 hover:bg-white/10 hover:-translate-y-1"
              >
                <Users size={22} className="text-weave-yellow" />
                <h3 className="mt-4 text-lg font-medium text-weave-cream leading-snug">Not sure whether founding is your next chapter?</h3>
                <p className="mt-2 text-sm text-weave-cream/60 leading-relaxed">
                  You don’t need to decide that today. Personal Clarity gives you ten structured weeks —
                  including your Index — to explore what you want, understand what you bring, and decide
                  whether building something of your own genuinely fits your life.
                </p>
                <p className="mt-3 text-sm text-weave-cream/80 leading-relaxed">
                  £499 including VAT. If you decide to build something, £2,501 secures the remaining
                  Business and External Clarity sections — £3,000 in total.
                </p>
                <div className="mt-4">
                  <DepositButton email={respondentEmail} assessmentId={id} plan="clarity" testId="results-clarity-card-button" />
                </div>
              </div>
            </FadeUp>
            <FadeUp delay={0.05}>
              <ProspectusDialog>
                <button
                  type="button"
                  data-testid="cta-prospectus"
                  className="group block w-full text-left bg-white/5 border border-white/15 rounded-2xl p-6 transition-all duration-300 hover:bg-white/10 hover:-translate-y-1"
                >
                  <Download size={22} className="text-weave-yellow" />
                  <h3 className="mt-4 text-lg font-medium text-weave-cream leading-snug">Download the Personal Clarity prospectus</h3>
                  <p className="mt-2 text-sm text-weave-cream/60 leading-relaxed">
                    See exactly what the ten-week opening phase involves and how it helps you decide
                    whether building something of your own is right for you.
                  </p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-weave-teal">
                    Share your email to download
                    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </button>
              </ProspectusDialog>
            </FadeUp>
          </div>

          <FadeUp delay={0.15} className="mt-12 border border-white/15 rounded-2xl p-8 flex flex-col md:flex-row md:items-center gap-6 justify-between" data-testid="report-actions">
            <div>
              <h3 className="text-lg font-medium">Your report, kept safe</h3>
              <p className="text-sm text-weave-cream/60 mt-1">Download the branded PDF, or have a copy emailed to you.</p>
              <Link
                to={`/assessment?email=${encodeURIComponent(respondentEmail)}&name=${encodeURIComponent(firstName || "")}`}
                data-testid="retake-button"
                className="group mt-3 inline-flex items-center gap-2 text-sm font-medium text-weave-yellow"
              >
                Retake the Index in a few months — watch your wheel change
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <a
                href={pdfUrl(id)}
                data-testid="download-pdf-button"
                className="inline-flex items-center justify-center gap-2 bg-weave-teal text-white hover:bg-weave-tealdeep transition-colors duration-300 rounded-full px-6 py-3.5 text-sm font-medium"
              >
                <Download size={16} /> Download PDF
              </a>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  data-testid="email-report-input"
                  className="flex-1 sm:w-52 rounded-full border border-white/20 bg-white/5 px-5 py-3 text-sm text-weave-cream placeholder:text-weave-cream/40 focus:border-weave-teal focus:outline-none"
                />
                <button
                  onClick={sendEmail}
                  disabled={emailBusy || emailSent}
                  data-testid="email-report-button"
                  className="inline-flex items-center gap-2 border border-weave-yellow text-weave-yellow hover:bg-weave-yellow hover:text-weave-ink transition-colors duration-300 rounded-full px-5 py-3 text-sm font-medium disabled:opacity-50"
                >
                  {emailSent ? <CheckCircle2 size={16} /> : <Mail size={16} />}
                  {emailSent ? "Sent" : "Email it"}
                </button>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Method */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeUp className="max-w-3xl" data-testid="method-note">
            <Overline>Method &amp; boundaries</Overline>
            <p className="mt-4 text-sm text-weave-slate leading-relaxed">
              The Founder Capacity Index asks you to rate 40 statements from 1 (strongly disagree) to
              5 (strongly agree), in relation to the last three months. Each of the seven capacities is
              scored as a mean from 1–5: below 2.75 suggests a priority for development, 2.75–4.24 a
              developing foundation, and 4.25 or above a well-developed capacity. This is a
              developmental reflection tool — not a clinical assessment, a psychometric test, or a
              prediction of business success. Your results are never used to exclude you from the
              Academy, and no automated decisions are made from them. You can request deletion of your
              data at any time by contacting The Weave.
            </p>
            <Link to="/" className="mt-8 inline-flex items-center gap-2 text-weave-teal font-medium group" data-testid="back-home-link">
              Back to the beginning
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
